import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { AccountTree, ArrowForward, PlaylistPlay, Refresh, Schedule, Search, Tv } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import { api } from '../../../services/api';
import type { AssignmentDeviceRow, AssignmentPlaylistRow } from '../../../types';

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString();
};

const statusColor = (status?: string) => {
  if (status === 'online' || status === 'active' || status === 'playing') return 'success';
  if (status === 'offline' || status === 'stopped') return 'default';
  if (status === 'error' || status === 'failed') return 'error';
  return 'warning';
};

const uniq = <T,>(items: T[]) => Array.from(new Set(items));

export const AssignmentsOverviewTab: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'draft' | 'archived'>('all');

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['assignments-overview'],
    queryFn: () => api.getAssignmentsOverview(),
    refetchInterval: 30000,
  });

  const normalizedSearch = search.trim().toLowerCase();
  const playlists = useMemo(() => {
    const rows = data?.playlists || [];
    return rows.filter((playlist) => {
      const matchesStatus = statusFilter === 'all' || playlist.status === statusFilter;
      const matchesSearch =
        !normalizedSearch ||
        playlist.name.toLowerCase().includes(normalizedSearch) ||
        playlist.target_devices.some((device) => device.name.toLowerCase().includes(normalizedSearch)) ||
        playlist.scheduled_events.some((event) => event.name.toLowerCase().includes(normalizedSearch));
      return matchesStatus && matchesSearch;
    });
  }, [data?.playlists, normalizedSearch, statusFilter]);

  const devices = useMemo(() => {
    const rows = data?.devices || [];
    if (!normalizedSearch) return rows;
    return rows.filter(
      (device) =>
        device.name.toLowerCase().includes(normalizedSearch) ||
        (device.current_playlist_name || '').toLowerCase().includes(normalizedSearch) ||
        device.scheduled_playlists.some((playlist) => playlist.name.toLowerCase().includes(normalizedSearch)),
    );
  }, [data?.devices, normalizedSearch]);

  const renderPlaylistTargets = (playlist: AssignmentPlaylistRow) => {
    const deviceNames = playlist.target_devices.map((device) => device.name);
    const groupNames = playlist.target_groups.map((group) => `${group.name} (${group.devices_count})`);
    const labels = [...groupNames, ...deviceNames];
    if (!labels.length) return <Typography variant="body2" color="text.secondary">Не назначен</Typography>;
    return (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {labels.slice(0, 6).map((label) => (
          <Chip key={label} size="small" label={label} />
        ))}
        {labels.length > 6 && <Chip size="small" label={`+${labels.length - 6}`} />}
      </Stack>
    );
  };

  const renderPlaylistSchedules = (playlist: AssignmentPlaylistRow) => {
    if (!playlist.scheduled_events.length) {
      return <Typography variant="body2" color="text.secondary">Нет расписаний</Typography>;
    }
    return (
      <Stack spacing={0.75}>
        {playlist.scheduled_events.slice(0, 3).map((event) => (
          <Box key={event.id}>
            <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body2" fontWeight={600}>{event.name}</Typography>
              <Chip size="small" label={event.status} color={statusColor(event.status) as any} />
              {event.is_active_now && <Chip size="small" label="Сейчас" color="success" />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {event.schedule_name || `Schedule #${event.schedule_id}`} • {event.target_name || 'без цели'} • {formatDateTime(event.start_time)}
            </Typography>
          </Box>
        ))}
        {playlist.scheduled_events.length > 3 && (
          <Typography variant="caption" color="text.secondary">
            Ещё событий: {playlist.scheduled_events.length - 3}
          </Typography>
        )}
      </Stack>
    );
  };

  const renderCurrentDevices = (playlist: AssignmentPlaylistRow) => {
    if (!playlist.current_devices.length) return <Typography variant="body2" color="text.secondary">Никто не играет</Typography>;
    return (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {playlist.current_devices.slice(0, 5).map((device) => (
          <Chip
            key={device.id}
            size="small"
            label={device.name}
            color={statusColor(device.playback_status) as any}
            variant={device.status === 'online' ? 'filled' : 'outlined'}
          />
        ))}
        {playlist.current_devices.length > 5 && <Chip size="small" label={`+${playlist.current_devices.length - 5}`} />}
      </Stack>
    );
  };

  const renderVisualMap = () => {
    const rows = playlists.slice(0, 8);
    if (!rows.length) {
      return null;
    }

    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <AccountTree color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Карта связей</Typography>
            <Typography variant="caption" color="text.secondary">
              Плейлист → расписания/ручные команды → целевые устройства и группы → фактическое проигрывание.
            </Typography>
          </Box>
        </Stack>

        <Stack spacing={1.25}>
          {rows.map((playlist) => {
            const scheduleLabels = playlist.scheduled_events.map((event) => event.name);
            const targetLabels = [
              ...playlist.target_groups.map((group) => `${group.name} (${group.devices_count})`),
              ...playlist.target_devices.map((device) => device.name),
            ];
            const playingLabels = playlist.current_devices.map((device) => device.name);

            return (
              <Box
                key={playlist.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '1.15fr 32px 1.3fr 32px 1.3fr 32px 1.15fr' },
                  gap: 1,
                  alignItems: 'stretch',
                }}
              >
                <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PlaylistPlay fontSize="small" color="primary" />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap title={playlist.name}>{playlist.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{playlist.items_count} файлов • {playlist.status}</Typography>
                    </Box>
                  </Stack>
                </Paper>
                <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'center' }}><ArrowForward fontSize="small" /></Box>
                <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Schedule fontSize="small" color="action" />
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {scheduleLabels.length
                        ? scheduleLabels.slice(0, 3).map((label) => <Chip key={label} size="small" label={label} />)
                        : <Typography variant="caption" color="text.secondary">Нет расписаний</Typography>}
                      {playlist.recent_manual_commands.length > 0 && <Chip size="small" color="info" label="ручной запуск" />}
                    </Stack>
                  </Stack>
                </Paper>
                <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'center' }}><ArrowForward fontSize="small" /></Box>
                <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Tv fontSize="small" color="action" />
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {targetLabels.length
                        ? targetLabels.slice(0, 4).map((label) => <Chip key={label} size="small" label={label} variant="outlined" />)
                        : <Typography variant="caption" color="text.secondary">Цели не заданы</Typography>}
                      {targetLabels.length > 4 && <Chip size="small" label={`+${targetLabels.length - 4}`} />}
                    </Stack>
                  </Stack>
                </Paper>
                <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', justifyContent: 'center' }}><ArrowForward fontSize="small" /></Box>
                <Paper variant="outlined" sx={{ p: 1.25, minWidth: 0, borderColor: playingLabels.length ? 'success.main' : 'divider' }}>
                  <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                    {playingLabels.length
                      ? playingLabels.slice(0, 3).map((label) => <Chip key={label} size="small" color="success" label={label} />)
                      : <Typography variant="caption" color="text.secondary">Сейчас не играет</Typography>}
                    {playingLabels.length > 3 && <Chip size="small" label={`+${playingLabels.length - 3}`} />}
                  </Stack>
                </Paper>
              </Box>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  const renderDevicePlan = (device: AssignmentDeviceRow) => {
    const scheduledNames = uniq(device.scheduled_playlists.map((playlist) => playlist.name));
    if (!scheduledNames.length) return <Typography variant="body2" color="text.secondary">Нет плановой привязки</Typography>;
    return (
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
        {device.scheduled_playlists.map((playlist) => (
          <Chip
            key={playlist.id}
            size="small"
            label={playlist.name}
            color={playlist.active_now ? 'success' : 'default'}
            variant={playlist.active_now ? 'filled' : 'outlined'}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Связи плейлистов, расписаний и устройств</Typography>
          <Typography variant="body2" color="text.secondary">
            Плановые назначения через расписания, текущие проигрывания и последние ручные запуски.
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={() => refetch()} disabled={isLoading}>
          Обновить
        </Button>
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.detail || (error as any)?.message || 'Не удалось загрузить связи'}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Поиск"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: { md: 360 } }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Статус плейлиста</InputLabel>
          <Select value={statusFilter} label="Статус плейлиста" onChange={(event) => setStatusFilter(event.target.value as any)}>
            <MenuItem value="all">Все</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <Chip label={`Плейлистов: ${data?.summary.playlists_total ?? 0}`} />
        <Chip label={`В расписаниях: ${data?.summary.playlists_scheduled ?? 0}`} color="primary" variant="outlined" />
        <Chip label={`Устройств: ${data?.summary.devices_total ?? 0}`} />
        <Chip label={`Играют: ${data?.summary.devices_playing ?? 0}`} color="success" variant="outlined" />
      </Stack>

      {renderVisualMap()}

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Плейлист</TableCell>
              <TableCell>Расписания</TableCell>
              <TableCell>Назначен на</TableCell>
              <TableCell>Сейчас играет</TableCell>
              <TableCell>Последние ручные команды</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center"><CircularProgress size={24} /></TableCell>
              </TableRow>
            )}
            {!isLoading && playlists.map((playlist) => (
              <TableRow key={playlist.id} hover>
                <TableCell sx={{ minWidth: 220 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">{playlist.name}</Typography>
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      <Chip size="small" label={playlist.status || 'unknown'} color={statusColor(playlist.status || undefined) as any} />
                      <Chip size="small" label={`${playlist.items_count} файлов`} variant="outlined" />
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 330 }}>{renderPlaylistSchedules(playlist)}</TableCell>
                <TableCell sx={{ minWidth: 260 }}>{renderPlaylistTargets(playlist)}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>{renderCurrentDevices(playlist)}</TableCell>
                <TableCell sx={{ minWidth: 230 }}>
                  {playlist.recent_manual_commands.length ? (
                    <Stack spacing={0.5}>
                      {playlist.recent_manual_commands.slice(0, 2).map((command) => (
                        <Typography key={command.id} variant="caption" color="text.secondary">
                          {command.device_name || `Device #${command.device_id}`} • {command.status} • {formatDateTime(command.created_at)}
                        </Typography>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Нет</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && !playlists.length && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">Связи не найдены</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="h6" sx={{ mb: 1 }}>Устройства и их план</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Устройство</TableCell>
              <TableCell>Текущий плейлист</TableCell>
              <TableCell>Плановые плейлисты</TableCell>
              <TableCell>Активное расписание</TableCell>
              <TableCell>Последний ручной запуск</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((device) => (
              <TableRow key={device.id} hover>
                <TableCell sx={{ minWidth: 220 }}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">{device.name}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Chip size="small" label={device.status} color={statusColor(device.status) as any} />
                      {device.group_name && <Chip size="small" label={device.group_name} variant="outlined" />}
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2">{device.current_playlist_name || '—'}</Typography>
                  {device.current_media_title && (
                    <Typography variant="caption" color="text.secondary">{device.current_media_title}</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>{renderDevicePlan(device)}</TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  <Typography variant="body2">{device.current_schedule_event_name || '—'}</Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>
                  {device.latest_manual_playlist_command ? (
                    <Typography variant="caption" color="text.secondary">
                      {device.latest_manual_playlist_command.playlist_name || `Playlist #${device.latest_manual_playlist_command.playlist_id}`} • {device.latest_manual_playlist_command.status}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Нет</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
